import React, { useState, useEffect, useRef } from 'react';
import api from '../../../api/axios';
import toast from 'react-hot-toast';
import Select from 'react-select';
import DatasetSelector from '../../Common/DatasetSelector';
import DiscretizationLayout from '../../Common/DiscretizationLayout';
import { Box, Paper, Typography, Button, TextField, MenuItem, Select as MuiSelect, FormControl, InputLabel } from '@mui/material';

// Plotly component for Avg per Segment Bar Chart with persisted titles
const AvgPerSegmentPlot = ({ plotData, defaultTitle, plotId, onTitleChange }) => {
  const plotRef = useRef(null);
  const [title, setTitle] = useState(() => {
    const saved = localStorage.getItem(`perf_avg_title_${plotId}`);
    return saved || defaultTitle || '';
  });
  const [xlabel, setXlabel] = useState(() => {
    const saved = localStorage.getItem(`perf_avg_xlabel_${plotId}`);
    return saved || 'Segment';
  });
  const [ylabel, setYlabel] = useState(() => {
    const saved = localStorage.getItem(`perf_avg_ylabel_${plotId}`);
    return saved || 'Average';
  });
  const [showEditor, setShowEditor] = useState(false);

  const renderPlot = (t, xl, yl) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    window.Plotly.newPlot(plotRef.current, [{
      x: plotData.segments,
      y: plotData.averages,
      type: 'bar',
      marker: { color: '#3498db', alpha: 0.8 },
      text: plotData.averages.map(v => v.toFixed(4)),
      textposition: 'auto'
    }], {
      title: t,
      xaxis: { title: xl, tickangle: -45 },
      yaxis: { title: yl },
      template: 'plotly_white',
      height: 450,
      margin: { t: 50, b: 100, l: 60, r: 20 }
    }, { responsive: true });
  };

  useEffect(() => { 
    if (plotData) renderPlot(title, xlabel, ylabel); 
  }, [plotData, title, xlabel, ylabel]);

  const applyTitles = () => {
    renderPlot(title, xlabel, ylabel);
    localStorage.setItem(`perf_avg_title_${plotId}`, title);
    localStorage.setItem(`perf_avg_xlabel_${plotId}`, xlabel);
    localStorage.setItem(`perf_avg_ylabel_${plotId}`, ylabel);
    if (onTitleChange) onTitleChange({ title, xlabel, ylabel });
    setShowEditor(false);
  };

  if (!plotData) return null;
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <h4 style={{ margin: 0, fontSize: '12px' }}>{title}</h4>
        <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '4px 8px', fontSize: '10px', background: 'none', border: '1px solid #e0e0e0', borderRadius: '4px', cursor: 'pointer' }}>
          {showEditor ? '✕' : '✏️ Edit'}
        </button>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '8px' }}>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <input type="text" value={xlabel} onChange={(e) => setXlabel(e.target.value)} placeholder="X Axis" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <input type="text" value={ylabel} onChange={(e) => setYlabel(e.target.value)} placeholder="Y Axis" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <button onClick={applyTitles} style={{ padding: '6px', fontSize: '11px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '450px' }}></div>
    </div>
  );
};

// Stability Plot component with persisted titles
const StabilityPlot = ({ plotData, defaultTitle, plotId, onTitleChange }) => {
  const plotRef = useRef(null);
  const [title, setTitle] = useState(() => {
    const saved = localStorage.getItem(`perf_stab_title_${plotId}`);
    return saved || defaultTitle || '';
  });
  const [xlabel, setXlabel] = useState(() => {
    const saved = localStorage.getItem(`perf_stab_xlabel_${plotId}`);
    return saved || 'Year';
  });
  const [ylabel, setYlabel] = useState(() => {
    const saved = localStorage.getItem(`perf_stab_ylabel_${plotId}`);
    return saved || 'Average';
  });
  const [showEditor, setShowEditor] = useState(false);

  const renderPlot = (t, xl, yl) => {
    if (!plotData || !plotRef.current || !window.Plotly) return;
    const traces = plotData.traces.map(tr => ({
      x: plotData.years,
      y: tr.values,
      type: 'scatter',
      mode: 'lines+markers',
      name: tr.segment,
      line: { width: 2 },
      marker: { size: 6 }
    }));
    window.Plotly.newPlot(plotRef.current, traces, {
      title: t,
      xaxis: { title: xl },
      yaxis: { title: yl },
      template: 'plotly_white',
      height: 450,
      legend: { orientation: 'h', y: -0.2 },
      margin: { t: 50, b: 100, l: 60, r: 20 }
    }, { responsive: true });
  };

  useEffect(() => { 
    if (plotData) renderPlot(title, xlabel, ylabel); 
  }, [plotData, title, xlabel, ylabel]);

  const applyTitles = () => {
    renderPlot(title, xlabel, ylabel);
    localStorage.setItem(`perf_stab_title_${plotId}`, title);
    localStorage.setItem(`perf_stab_xlabel_${plotId}`, xlabel);
    localStorage.setItem(`perf_stab_ylabel_${plotId}`, ylabel);
    if (onTitleChange) onTitleChange({ title, xlabel, ylabel });
    setShowEditor(false);
  };

  if (!plotData) return null;
  return (
    <div style={{ marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <h4 style={{ margin: 0, fontSize: '12px' }}>{title}</h4>
        <button onClick={() => setShowEditor(!showEditor)} style={{ padding: '4px 8px', fontSize: '10px', background: 'none', border: '1px solid #e0e0e0', borderRadius: '4px', cursor: 'pointer' }}>
          {showEditor ? '✕' : '✏️ Edit'}
        </button>
      </div>
      {showEditor && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px', background: '#f8f9fa', borderRadius: '6px', marginBottom: '8px' }}>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <input type="text" value={xlabel} onChange={(e) => setXlabel(e.target.value)} placeholder="X Axis" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <input type="text" value={ylabel} onChange={(e) => setYlabel(e.target.value)} placeholder="Y Axis" style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #ddd' }} />
          <button onClick={applyTitles} style={{ padding: '6px', fontSize: '11px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Apply</button>
        </div>
      )}
      <div ref={plotRef} style={{ width: '100%', height: '450px' }}></div>
    </div>
  );
};

// Tests Panel Component
const TestsPanel = ({ results }) => {
  const [testType, setTestType] = useState('welch');

  const getData = () => {
    switch(testType) {
      case 'welch': return results?.welch || [];
      case 'tukey': return results?.tukey || [];
      case 'games_howell': return results?.games_howell || [];
      default: return [];
    }
  };

  const data = getData();
  const testNames = { welch: "Welch's t-test", tukey: 'Tukey HSD', games_howell: 'Games-Howell' };

  if (data.length === 0) {
    return <p style={{ fontSize: '11px', color: '#7f8c8d', padding: '10px' }}>No test results available</p>;
  }

  const headers = Object.keys(data[0]);

  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        <select value={testType} onChange={(e) => setTestType(e.target.value)} style={{ padding: '6px', fontSize: '11px', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
          <option value="welch">Welch's t-test</option>
          <option value="tukey">Tukey HSD</option>
          <option value="games_howell">Games-Howell</option>
        </select>
      </div>

      <h4 style={{ fontSize: '12px', marginBottom: '8px' }}>{testNames[testType]}</h4>

      <div className="table-scroll" style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
        <table style={{ width: '100%', fontSize: '10px', borderCollapse: 'collapse' }}>
          <thead style={{ position: 'sticky', top: 0, background: '#34495e', color: 'white', zIndex: 10 }}>
            <tr>
              {headers.map(h => <th key={h} style={{ padding: '8px' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.slice(0, 50).map((row, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #e0e0e0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                {headers.map(h => (
                  <td key={h} style={{ padding: '6px' }}>
                    {typeof row[h] === 'number' ? row[h].toFixed(4) : row[h]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PerformanceTesting = () => {
  const [step, setStep] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyProgress, setApplyProgress] = useState(null);
  const [savingColumn, setSavingColumn] = useState(false);
  const [results, setResults] = useState(null);
  const [datasetInfo, setDatasetInfo] = useState(null);
  const [datasetLoaded, setDatasetLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('performance');
  const [savedModels, setSavedModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedModelDetails, setSelectedModelDetails] = useState(null);
  const [outputColumnName, setOutputColumnName] = useState('cart_leaf');
  const [appliedModel, setAppliedModel] = useState(false);
  const [saveColumnName, setSaveColumnName] = useState('');
  const [showSaveSection, setShowSaveSection] = useState(false);

  const [formData, setFormData] = useState({
    segment_field: null,
    target_variable: null,
    default_date: null
  });

  // ========== ADDED: Analysis state variables ==========
  const [savedAnalysisId, setSavedAnalysisId] = useState(null);
  const [analysisStatus, setAnalysisStatus] = useState('draft');
  const [analysisDescription, setAnalysisDescription] = useState('');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [isCommentsExpanded, setIsCommentsExpanded] = useState(true);
  const [updatingMetadata, setUpdatingMetadata] = useState(false);
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false);
  const [allDatasets, setAllDatasets] = useState([]);

  // ========== ADDED: Mode detection ==========
  const mode = new URLSearchParams(window.location.search).get('mode');
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit' || !mode;

  const columnOptions = columns.map(c => ({ value: c, label: c }));

  useEffect(() => {
    if (!window.Plotly) {
      const script = document.createElement('script');
      script.src = 'https://cdn.plot.ly/plotly-2.27.0.min.js';
      document.head.appendChild(script);
    }
    loadSavedModels();
  }, []);

  // ========== ADDED: Load saved analysis on mount ==========
  useEffect(() => {
    const analysisId = new URLSearchParams(window.location.search).get('analysis_id');
    if (analysisId && !isNaN(parseInt(analysisId))) {
      loadSavedAnalysis(parseInt(analysisId));
    }
  }, []);

  const fetchDatasets = async () => {
    try {
      const projectId = new URLSearchParams(window.location.search).get('project_id');
      if (!projectId) {
        setAllDatasets([]);
        return;
      }
      const res = await api.get(`/api/v1/tables/project/${projectId}/datasets`);
      const datasets = res.data.map(ds => ({
        id: ds.id,
        name: ds.original_filename,
        rows: ds.row_count,
        columns: ds.column_count
      }));
      setAllDatasets(datasets);
    } catch (err) {
      console.error('Failed to load datasets', err);
    }
  };
  useEffect(() => {
    fetchDatasets();
  }, []);

  // ========== ADDED: Load saved analysis function ==========
  const loadSavedAnalysis = async (analysisId) => {
    setIsLoadingAnalysis(true);
    try {
      const res = await api.get(`/analyses/${analysisId}`);
      const analysis = res.data;
      
      if (analysis.status) setAnalysisStatus(analysis.status);
      if (analysis.description) setAnalysisDescription(analysis.description);
      setSavedAnalysisId(analysis.id);
      if (analysis.comments && Array.isArray(analysis.comments)) setComments(analysis.comments);
      
      if (analysis.input_parameters) {
        if (analysis.input_parameters.dataset_id) {
          setDatasetInfo({
            id: analysis.input_parameters.dataset_id,
            name: analysis.input_parameters.dataset_name,
            rows: analysis.input_parameters.rows,
            columns: analysis.input_parameters.columns
          });
          setDatasetLoaded(true);
          setColumns(analysis.input_parameters.columns || []);
        }
        
        setFormData({
          segment_field: analysis.input_parameters.segment_field ? { value: analysis.input_parameters.segment_field, label: analysis.input_parameters.segment_field } : null,
          target_variable: analysis.input_parameters.target_variable ? { value: analysis.input_parameters.target_variable, label: analysis.input_parameters.target_variable } : null,
          default_date: analysis.input_parameters.default_date ? { value: analysis.input_parameters.default_date, label: analysis.input_parameters.default_date } : null
        });
        
        if (analysis.input_parameters.results) setResults(analysis.input_parameters.results);
        if (analysis.input_parameters.outputColumnName) setOutputColumnName(analysis.input_parameters.outputColumnName);
        if (analysis.input_parameters.appliedModel) setAppliedModel(analysis.input_parameters.appliedModel);
      }
      
      if (analysis.output_result) {
        setStep(3);
        toast.success('Analysis loaded with results');
      } else if (analysis.input_parameters) {
        setStep(2);
        toast.success('Analysis loaded. Continue to run analysis steps.');
      }
    } catch (err) {
      toast.error('Failed to load analysis');
    } finally {
      setIsLoadingAnalysis(false);
    }
  };

  // ========== ADDED: Save Analysis function ==========
  const handleSaveAnalysis = async () => {
    if (!datasetInfo) {
      toast.error('No dataset selected');
      return;
    }
    const projectId = new URLSearchParams(window.location.search).get('project_id');
    if (!projectId) {
      toast.error('No project selected');
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        project_id: parseInt(projectId),
        module_name: 'performance_testing',
        description: analysisDescription,
        input_parameters: {
          dataset_id: datasetInfo.id,
          dataset_name: datasetInfo.name,
          rows: datasetInfo.rows,
          columns: datasetInfo.columns,
          segment_field: formData.segment_field?.value || '',
          target_variable: formData.target_variable?.value || '',
          default_date: formData.default_date?.value || '',
          outputColumnName: outputColumnName,
          appliedModel: appliedModel,
          results: results
        },
        output_result: results,
        status: analysisStatus,
        comments: comments
      };
      const res = await api.post('/analyses/', payload);
      toast.success(`Analysis saved with ID: ${res.data.id}`);
      setSavedAnalysisId(res.data.id);
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save analysis');
    } finally {
      setLoading(false);
    }
  };

  // ========== ADDED: Update Metadata function ==========
  const handleUpdateMetadata = async () => {
    if (!savedAnalysisId) {
      toast.error('No analysis saved yet');
      return;
    }
    setUpdatingMetadata(true);
    try {
      await api.patch(`/analyses/${savedAnalysisId}`, {
        status: analysisStatus,
        description: analysisDescription
      });
      toast.success('Status and description updated');
    } catch (err) {
      toast.error('Failed to update');
    } finally {
      setUpdatingMetadata(false);
    }
  };

  // ========== ADDED: Add Comment function ==========
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    const commentObj = {
      id: Date.now(),
      text: newComment,
      created_at: new Date().toISOString(),
      user_id: localStorage.getItem('user_id') || 'user',
      user_email: localStorage.getItem('user_email') || 'Unknown'
    };
    const updatedComments = [...comments, commentObj];
    
    if (savedAnalysisId) {
      setSavingComment(true);
      try {
        await api.patch(`/analyses/${savedAnalysisId}`, { comments: updatedComments });
        setComments(updatedComments);
        setNewComment('');
        toast.success('Comment added');
      } catch (err) {
        toast.error('Failed to add comment');
      } finally {
        setSavingComment(false);
      }
    } else {
      setComments(updatedComments);
      setNewComment('');
      toast.success('Comment added (will be saved when you save the analysis)');
    }
  };

  const loadSavedModels = async () => {
    try {
      const response = await api.get('/api/cart/list-models');
      setSavedModels(response.data.models || []);
    } catch (error) {
      console.error('Error loading models:', error);
    }
  };

  const hdl = (data) => {
    setColumns(data.columns || []);
    setDatasetInfo({
      id: data.id,
      name: `Dataset ${data.id}`,
      rows: data.rows,
      columns: data.columns
    });
    setDatasetLoaded(true);
    toast.success(`Loaded ${data.rows?.toLocaleString()} rows`);
  };

  // Update name when allDatasets is loaded
  useEffect(() => {
    if (datasetInfo && allDatasets.length > 0) {
      const datasetFromList = allDatasets.find(d => d.id === datasetInfo.id);
      if (datasetFromList && datasetFromList.name !== datasetInfo.name) {
        setDatasetInfo(prev => ({ ...prev, name: datasetFromList.name }));
      }
    }
  }, [allDatasets, datasetInfo]);

  const hfc = (f, v) => setFormData(p => ({ ...p, [f]: v }));

  const handleModelSelect = (modelFilename) => {
    setSelectedModel(modelFilename);
    const model = savedModels.find(m => m.filename === modelFilename);
    setSelectedModelDetails(model);
  };

  const applyModelToDataset = async () => {
    if (!selectedModel || !datasetInfo?.id) {
      toast.error('Please select a model and ensure dataset is loaded');
      return;
    }
    if (!outputColumnName.trim()) {
      toast.error('Please enter an output column name');
      return;
    }
    setLoading(true);
    setApplyProgress('Starting model application for ' + datasetInfo.rows?.toLocaleString() + ' rows...');
    
    const startTime = Date.now();
    
    const progressInterval = setInterval(async () => {
      try {
        const progressRes = await api.get(`/api/cart/apply-progress/${datasetInfo.id}`);
        if (progressRes.data.progress > 0) {
          setApplyProgress(`${progressRes.data.message} (${progressRes.data.progress}%)`);
        }
      } catch (e) {}
    }, 2000);
    
    try {
      const response = await api.post('/api/cart/apply-model', {
        model_filename: selectedModel,
        dataset_id: datasetInfo.id,
        output_column: outputColumnName
      });
      
      clearInterval(progressInterval);
      
      const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(1);
      const minutes = Math.floor(elapsedTime / 60);
      const seconds = elapsedTime % 60;
      const timeStr = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
      
      toast.success(`${response.data.message} (${timeStr})`);
      setAppliedModel(true);
      
      setApplyProgress('Reloading dataset...');
      const refreshResponse = await api.post('/api/performance/select-dataset', { dataset_id: datasetInfo.id });
      setColumns(refreshResponse.data.columns || []);
      
      setFormData(prev => ({ ...prev, segment_field: { value: outputColumnName, label: outputColumnName } }));
      toast.success(`Column '${outputColumnName}' added and selected as segment field`);
    } catch (err) {
      clearInterval(progressInterval);
      toast.error(err.response?.data?.detail || 'Failed to apply model');
    } finally {
      setLoading(false);
      setApplyProgress(null);
    }
  };

  const saveParams = async () => {
    if (!formData.segment_field || !formData.target_variable) {
      toast.error('Please select segment field and target variable');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/performance/save-params', {
        segment_field: formData.segment_field.value,
        target_variable: formData.target_variable.value,
        default_date: formData.default_date?.value || ''
      });
      toast.success('Parameters saved');
      setStep(2);
    } catch (err) {
      toast.error('Failed to save parameters');
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/performance/run-analysis', {
        segment_field: formData.segment_field.value,
        target_variable: formData.target_variable.value,
        default_date: formData.default_date?.value || ''
      });
      setResults(response.data.results);
      toast.success(response.data.message);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };

  const exportResults = async () => {
    const customTitles = {
      avg_title: localStorage.getItem('perf_avg_title_avg_segment') || '',
      avg_xlabel: localStorage.getItem('perf_avg_xlabel_avg_segment') || 'Segment',
      avg_ylabel: localStorage.getItem('perf_avg_ylabel_avg_segment') || 'Average',
      stab_title: localStorage.getItem('perf_stab_title_stability') || '',
      stab_xlabel: localStorage.getItem('perf_stab_xlabel_stability') || 'Year',
      stab_ylabel: localStorage.getItem('perf_stab_ylabel_stability') || 'Average'
    };
    try {
      const response = await api.post('/api/performance/export-with-titles', { custom_titles: customTitles }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'performance_testing.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to export results');
    }
  };

  const sc = (t) => {
    if (t === 0) setStep(0);
    else if (t === 1 && datasetLoaded) setStep(1);
    else if (t === 2 && formData.segment_field && formData.target_variable) setStep(2);
    else if (t === 3 && results) setStep(3);
  };

  const cs = {
    control: (b) => ({ ...b, minHeight: '34px', height: '34px', borderColor: '#e0e0e0', fontSize: '12px', boxShadow: 'none' }),
    valueContainer: (b) => ({ ...b, padding: '0 8px' }),
    placeholder: (b) => ({ ...b, color: '#95a5a6', fontSize: '12px' }),
    option: (b, s) => ({ ...b, backgroundColor: s.isSelected ? '#1abc9c' : s.isFocused ? '#e8f8f5' : 'white', fontSize: '12px', padding: '8px 12px' })
  };

  const tabs = ['Performance', 'Avg per Segment', 'Stability', 'Statistical Tests'];

  const avgPlotData = results?.avg_leaf_data ? {
    segments: results.avg_leaf_data.map(d => d.Segment),
    averages: results.avg_leaf_data.map(d => d.Average)
  } : null;

  const stabilityPlotData = results?.stability_data && results.stability_data.length > 0 ? {
    years: results.stability_data.map(d => d.year),
    traces: Object.keys(results.stability_data[0])
      .filter(k => k !== 'year')
      .map(segment => ({
        segment: segment,
        values: results.stability_data.map(d => d[segment])
      }))
  } : null;

  return (
    <DiscretizationLayout title="Performance & Testing" step={step} onStepClick={sc} datasetLoaded={datasetLoaded} results={results}>
      {/* ========== ADDED: View mode banner ========== */}
      {isViewMode && results && (
        <div style={{ background: '#e8f8f5', padding: '8px 16px', borderRadius: '8px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#1abc9c' }}>👁️ Viewing saved analysis (read-only)</span>
          <button
            onClick={() => {
              const url = new URL(window.location.href);
              url.searchParams.set('mode', 'edit');
              window.location.href = url.toString();
            }}
            style={{ padding: '4px 12px', fontSize: '12px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Modify
          </button>
        </div>
      )}

      {step === 0 && (
        <>
          <DatasetSelector projectId={new URLSearchParams(window.location.search).get("project_id")} apiPrefix="/api/performance" onDatasetLoaded={hdl} />
          
          {/* ========== ADDED: Dataset info display ========== */}
          {datasetInfo && (
            <div style={{ 
              marginTop: '16px', 
              marginBottom: '16px', 
              padding: '12px', 
              background: '#e8f8f5', 
              borderRadius: '8px',
              borderLeft: '4px solid #1abc9c'
            }}>
              <div style={{ fontSize: '13px', color: '#2c3e50', fontWeight: 500 }}>
                📌 Currently selected dataset:
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#1abc9c', marginTop: '4px' }}>
                {datasetInfo.name}
              </div>
              <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '4px' }}>
                {datasetInfo.rows?.toLocaleString()} rows • {datasetInfo.columns?.length || 0} columns
              </div>
            </div>
          )}

          {columns.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '20px' }}>
              {/* Apply Saved CART Model Section */}
              <div style={{ background: '#f0f8ff', padding: '15px', borderRadius: '6px', border: '1px solid #d1ecf1' }}>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#0c5460', marginBottom: '8px', display: 'block' }}>
                  🎯 Apply Saved CART Model (Optional)
                </label>
                <p style={{ fontSize: '10px', color: '#0c5460', marginBottom: '8px' }}>
                  Apply a previously saved CART model to generate leaf node classes
                </p>
                
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Select Model</label>
                  <select 
                    value={selectedModel || ''} 
                    onChange={(e) => handleModelSelect(e.target.value)}
                    style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #e0e0e0', marginBottom: '8px' }}
                    disabled={isViewMode}
                  >
                    <option value="">-- Select a saved model --</option>
                    {savedModels.map(model => (
                      <option key={model.filename} value={model.filename}>
                        {model.name} - Target: {model.target} ({new Date(model.created_at).toLocaleDateString()})
                      </option>
                    ))}
                  </select>
                  
                  {selectedModelDetails && (
                    <div style={{ background: '#e8f4f8', padding: '10px', borderRadius: '4px', marginBottom: '8px', fontSize: '11px' }}>
                      <div><strong>📊 Target Variable:</strong> {selectedModelDetails.target}</div>
                      <div><strong>🔧 Risk Drivers ({selectedModelDetails.n_predictors}):</strong></div>
                      <div style={{ maxHeight: '100px', overflow: 'auto', marginTop: '4px' }}>
                        {selectedModelDetails.predictors?.slice(0, 10).map((p, i) => (
                          <span key={i} style={{ display: 'inline-block', background: '#e0e0e0', padding: '2px 6px', margin: '2px', borderRadius: '3px', fontSize: '10px' }}>
                            {p}
                          </span>
                        ))}
                        {selectedModelDetails.predictors?.length > 10 && (
                          <span style={{ fontSize: '10px', color: '#666' }}> +{selectedModelDetails.predictors.length - 10} more</span>
                        )}
                      </div>
                      <div><strong>📅 Created:</strong> {new Date(selectedModelDetails.created_at).toLocaleString()}</div>
                    </div>
                  )}
                </div>
                
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '11px', fontWeight: 500, marginBottom: '4px', display: 'block' }}>Output Column Name</label>
                  <input
                    type="text"
                    value={outputColumnName}
                    onChange={(e) => setOutputColumnName(e.target.value)}
                    placeholder="e.g., cart_leaf, segment_prediction"
                    style={{ width: '100%', padding: '8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #e0e0e0' }}
                    disabled={isViewMode}
                  />
                  <p style={{ fontSize: '9px', color: '#0c5460', marginTop: '4px' }}>
                    This column will contain the leaf node assignments from the model
                  </p>
                </div>
                
                <button 
                  onClick={applyModelToDataset} 
                  disabled={loading || !selectedModel || isViewMode}
                  style={{ width: '100%', padding: '8px 16px', fontSize: '12px', background: (loading || !selectedModel || isViewMode) ? '#95a5a6' : '#17a2b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  {loading ? 'Applying...' : 'Apply Model →'}
                </button>
                
                {applyProgress && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#0c5460', background: '#d1ecf1', padding: '6px', borderRadius: '4px' }}>
                    ⏳ {applyProgress}
                  </div>
                )}
                
                {appliedModel && (
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#155724', background: '#d4edda', padding: '6px', borderRadius: '4px' }}>
                    ✅ Model applied! Column '{outputColumnName}' added and selected as segment field
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button onClick={() => setStep(1)} style={{ padding: '10px 28px', fontSize: '13px', background: '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                  Continue →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {step === 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#e74c3c', marginBottom: '6px', display: 'block' }}>Segment Field *</label>
            <Select 
              options={columnOptions} 
              value={formData.segment_field} 
              onChange={(v) => hfc('segment_field', v)} 
              placeholder="Select segment column (e.g., cart_leaf from applied model)..."
              styles={cs} 
              isDisabled={isViewMode}
            />
            <p style={{ fontSize: '10px', color: '#7f8c8d', marginTop: '4px' }}>
              The column containing segment/leaf assignments
            </p>
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#e74c3c', marginBottom: '6px', display: 'block' }}>Target Variable *</label>
            <Select 
              options={columnOptions} 
              value={formData.target_variable} 
              onChange={(v) => hfc('target_variable', v)} 
              placeholder="Select target column..." 
              styles={cs} 
              isDisabled={isViewMode}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', fontWeight: 600, color: '#7f8c8d', marginBottom: '6px', display: 'block' }}>Claims Date (Optional)</label>
            <Select 
              options={columnOptions} 
              value={formData.default_date} 
              onChange={(v) => hfc('default_date', v)} 
              isClearable 
              placeholder="Select date column for stability analysis..." 
              styles={cs} 
              isDisabled={isViewMode}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
            <button onClick={saveParams} disabled={loading || isViewMode} style={{ padding: '10px 28px', fontSize: '13px', background: loading || isViewMode ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              {loading ? 'Saving...' : 'Continue →'}
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '6px', marginBottom: '20px' }}>
            <h4 style={{ fontSize: '13px', marginBottom: '10px' }}>Review Parameters</h4>
            <p><strong>Segment Field:</strong> {formData.segment_field?.label}</p>
            <p><strong>Target Variable:</strong> {formData.target_variable?.label}</p>
            {formData.default_date?.label && <p><strong>Date Column:</strong> {formData.default_date?.label}</p>}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => setStep(1)} style={{ padding: '8px 20px', fontSize: '12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            <button onClick={runAnalysis} disabled={loading || isViewMode} style={{ padding: '8px 24px', fontSize: '12px', background: loading || isViewMode ? '#95a5a6' : '#1abc9c', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
              {loading ? 'Running...' : '🚀 Run Performance Testing'}
            </button>
          </div>
        </div>
      )}

      {step === 3 && results && (
        <div>
          <div style={{ display: 'flex', gap: '2px', marginBottom: '16px', borderBottom: '1px solid #e0e0e0', paddingBottom: '8px', flexWrap: 'wrap' }}>
            {tabs.map(t => {
              const tk = t.toLowerCase().replace(/\s+/g, '-');
              return (
                <button key={t} onClick={() => setActiveTab(tk)} style={{ padding: '6px 10px', fontSize: '10px', background: activeTab === tk ? '#1abc9c' : 'transparent', color: activeTab === tk ? 'white' : '#7f8c8d', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                  {t}
                </button>
              );
            })}
          </div>

          {activeTab === 'performance' && results.performance_data && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
              {results.performance_data.map((item, i) => (
                <div key={i} style={{ background: 'white', padding: '20px', borderRadius: '8px', textAlign: 'center', border: '1px solid #e0e0e0', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: i === 0 ? '#e67e22' : i === 1 ? '#3498db' : '#2ecc71' }}>{item.Value}</div>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '8px', fontWeight: 600 }}>{item.Metric}</div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'avg-per-segment' && avgPlotData && (
            <AvgPerSegmentPlot 
              plotData={avgPlotData} 
              defaultTitle={`Average ${formData.target_variable?.label} per Segment`}
              plotId="avg_segment"
            />
          )}

          {activeTab === 'stability' && stabilityPlotData && stabilityPlotData.traces.length > 0 && (
            <StabilityPlot 
              plotData={stabilityPlotData} 
              defaultTitle="Segment Stability Over Years"
              plotId="stability"
            />
          )}

          {activeTab === 'statistical-tests' && (
            <TestsPanel results={results} />
          )}

          {/* Save Predictions to Dataset Button */}
          {appliedModel && (
            <div style={{ marginTop: '20px' }}>
              <button 
                onClick={() => setShowSaveSection(!showSaveSection)} 
                style={{ padding: '6px 12px', fontSize: '12px', background: 'none', border: '1px solid #e0e0e0', borderRadius: '4px', cursor: 'pointer', marginBottom: showSaveSection ? '10px' : '0' }}
              >
                {showSaveSection ? '✕ Cancel' : '💾 Save Segment Field to Dataset'}
              </button>
              
              {showSaveSection && (
                <div style={{ padding: '12px', background: '#f8f9fa', borderRadius: '6px' }}>
                  <p style={{ fontSize: '11px', marginBottom: '8px', color: '#2c3e50' }}>
                    Save the current segment field <strong>'{formData.segment_field?.label}'</strong> as a new column in your dataset
                  </p>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <input 
                      type="text" 
                      value={saveColumnName} 
                      onChange={(e) => setSaveColumnName(e.target.value)} 
                      placeholder="New column name"
                      style={{ flex: 1, padding: '8px', fontSize: '12px', borderRadius: '4px', border: '1px solid #e0e0e0' }}
                      disabled={isViewMode}
                    />
                    <button 
                      onClick={async () => {
                        if (!saveColumnName.trim()) {
                          toast.error('Please enter a column name');
                          return;
                        }
                        setSavingColumn(true);
                        try {
                          toast.success(`Column '${saveColumnName}' would be saved`);
                          setShowSaveSection(false);
                          setSaveColumnName('');
                        } catch (err) {
                          toast.error('Failed to save column');
                        } finally {
                          setSavingColumn(false);
                        }
                      }} 
                      disabled={savingColumn || !saveColumnName.trim() || isViewMode}
                      style={{ padding: '8px 16px', fontSize: '12px', background: (savingColumn || !saveColumnName.trim() || isViewMode) ? '#95a5a6' : '#27ae60', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      {savingColumn ? 'Saving...' : 'Save to Dataset'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
            <button onClick={() => setStep(2)} style={{ padding: '8px 20px', fontSize: '12px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>← Back</button>
            
          </div>

          {/* ========== ADDED: Comments Section ========== */}
          <div style={{ marginTop: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
            <div 
              style={{ 
                background: '#f5f7fa', 
                padding: '12px 16px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                fontWeight: 600,
                fontSize: '14px',
                color: '#2c3e50'
              }}
              onClick={() => setIsCommentsExpanded(!isCommentsExpanded)}
            >
              <span>💬 Comments & Discussions ({comments.length})</span>
              <span style={{ fontSize: '20px', fontWeight: 'bold' }}>
                {isCommentsExpanded ? '−' : '+'}
              </span>
            </div>
            
            {isCommentsExpanded && (
              <div style={{ padding: '16px', background: 'white' }}>
                <div style={{ maxHeight: '300px', overflow: 'auto', marginBottom: '16px' }}>
                  {comments.length === 0 ? (
                    <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                      No comments yet. Be the first to add a comment.
                    </Typography>
                  ) : (
                    comments.map((comment) => (
                      <div
                        key={comment.id}
                        style={{
                          padding: '10px 12px',
                          marginBottom: '8px',
                          background: '#f8f9fa',
                          borderRadius: '8px',
                          borderLeft: '3px solid #1abc9c'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '11px', fontWeight: 600, color: '#2c3e50' }}>
                            {comment.user_email || comment.user_id || 'Unknown'}
                          </span>
                          <span style={{ fontSize: '10px', color: '#95a5a6' }}>
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: '#34495e', whiteSpace: 'pre-wrap' }}>
                          {comment.text}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment or question..."
                    style={{
                      flex: 1,
                      padding: '10px',
                      fontSize: '13px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      resize: 'vertical',
                      minHeight: '60px',
                      fontFamily: 'inherit'
                    }}
                  />
                  <button
                    onClick={handleAddComment}
                    disabled={savingComment || !newComment.trim()}
                    style={{
                      padding: '8px 20px',
                      background: (savingComment || !newComment.trim()) ? '#95a5a6' : '#1abc9c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (savingComment || !newComment.trim()) ? 'not-allowed' : 'pointer',
                      fontSize: '13px',
                      height: 'fit-content'
                    }}
                  >
                    {savingComment ? 'Sending...' : 'Add Comment'}
                  </button>
                </div>
                
                {!savedAnalysisId && comments.length > 0 && (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    💡 Comments will be permanently saved when you click "Save Analysis"
                  </Typography>
                )}
              </div>
            )}
          </div>

          {/* ========== ADDED: Professional Action Area ========== */}
          <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <MuiSelect
                  value={analysisStatus}
                  onChange={(e) => setAnalysisStatus(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="draft">Draft</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="validated">Validated</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </MuiSelect>
              </FormControl>
            </div>
            
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description"
              value={analysisDescription}
              onChange={(e) => setAnalysisDescription(e.target.value)}
              placeholder="Describe the purpose, methodology, or key findings of this performance testing analysis..."
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={() => setStep(0)}
                disabled={isViewMode}
                size="small"
              >
                New Analysis
              </Button>
              
              <Button
                variant="contained"
                onClick={exportResults}
                size="small"
                sx={{ bgcolor: '#27ae60', '&:hover': { bgcolor: '#219a52' } }}
              >
                📥 Export
              </Button>
              
              <Button
                variant="contained"
                onClick={handleSaveAnalysis}
                disabled={!results || isViewMode}
                size="small"
                sx={{ bgcolor: results && !isViewMode ? '#1abc9c' : '#95a5a6', '&:hover': { bgcolor: '#16a085' } }}
              >
                💾 Save Analysis
              </Button>
            </Box>
          </div>
        </div>
      )}
    </DiscretizationLayout>
  );
};

export default PerformanceTesting;